import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Checkbox, DateTimePicker, Input, Modal } from '@/components/ui'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import {
  CoinVoucher,
  deleteCoinVoucher,
  getCoinVoucherPdf,
  updateCoinVoucher,
} from '@/core/services/voucher'
import { MessageBox } from '../messages/MessageBox'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { FileDown } from 'lucide-react'

interface ViewVoucherFormValues {
  name: string
  value?: number
  usagePerVoucher?: 'Unlimited' | 'Limited'
  limitAmount?: number
  nominated?: 'Yes' | 'No'
  expiryDate: string
  active: boolean
}

export interface RedeemVoucherViewModalProps {
  voucher: CoinVoucher | null
  open: boolean
  onClose: () => void
  onUpdated?: () => void
  currency: string
}

export const RedeemVoucherViewModal = ({
  voucher,
  open,
  onClose,
  onUpdated,
  currency,
}: RedeemVoucherViewModalProps) => {
  const [submitting, setSubmitting] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const schema = yup.object().shape({
    name: yup.string().required('Name is required'),
    value: yup.number().optional(),
    usagePerVoucher: yup
      .mixed<'Unlimited' | 'Limited'>()
      .oneOf(['Unlimited', 'Limited'])
      .optional(),
    limitAmount: yup
      .number()
      .optional()
      .transform((v) => (isNaN(v) ? undefined : v)),
    nominated: yup.mixed<'Yes' | 'No'>().oneOf(['Yes', 'No']).optional(),
    expiryDate: yup.string().required('Expiry date is required'),
    active: yup.boolean().required(),
  })

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ViewVoucherFormValues>({
    mode: 'onChange',
    resolver: yupResolver<ViewVoucherFormValues>(schema),
    values: voucher
      ? {
          name: voucher.label,
          value: voucher.amount,
          usagePerVoucher: voucher.unlimited ? 'Unlimited' : 'Limited',
          limitAmount: voucher.unlimited ? undefined : voucher.voucherLimit,
          nominated: voucher.nominated ? 'Yes' : 'No',
          expiryDate: formatDateTime(voucher.expirationDate),
          active: voucher.published,
        }
      : undefined,
  })

  const watchUsagePerVoucher = watch('usagePerVoucher')
  const watchExpiryDate = watch('expiryDate')
  const watchActive = watch('active')

  const handleClose = () => {
    reset()
    onClose()
  }

  const submit = async (values: ViewVoucherFormValues) => {
    if (!voucher) return
    try {
      setSubmitting(true)
      const { status } = await updateCoinVoucher(voucher.id, {
        label: values.name,
        expirationDate: values.expiryDate
          ? new Date(values.expiryDate).valueOf()
          : 0,
        published: values.active,
      })
      if (status === 200) {
        handleClose()
        onUpdated?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Voucher updated successfully')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteVoucher = async () => {
    if (!voucher) return
    try {
      const { status } = await deleteCoinVoucher(voucher.id)
      if (status === 200 || status === 204) {
        handleClose()
        onUpdated?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Voucher deleted successfully')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleDownloadPdf = async () => {
    if (!voucher) return
    try {
      const { data, status } = await getCoinVoucherPdf(voucher.id)
      if (status === 200) {
        const url = window.URL.createObjectURL(data)
        window.open(url, '_blank')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  if (!open || !voucher) return null

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Voucher" subtitle="View" />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            <Button
              onClick={() => setDeleteModal(true)}
              variant="secondary"
              type="button"
              className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
            >
              Delete Voucher
              <Icon
                icon="close-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="view-voucher-form"
              type="submit"
              variant="primary"
              loading={submitting}
            >
              {submitting ? 'Saving ...' : 'Save'}
            </Button>
          </div>
        }
      >
        <form
          id="view-voucher-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="WINTER28 GIVEAWAY"
            />
          </FormGroup>

          <FormGroup
            label={`Value in ${currency}`}
            error={errors?.value?.message}
          >
            <Input
              isInvalid={!!errors?.value?.message}
              {...register('value')}
              type="number"
              placeholder="500"
              disabled
            />
          </FormGroup>

          <FormGroup
            label="Usage per Voucher"
            error={errors?.usagePerVoucher?.message}
          >
            <Controller
              name="usagePerVoucher"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    { label: 'Unlimited', value: 'Unlimited' },
                    { label: 'Limited', value: 'Limited' },
                  ]}
                  placeholder="Select"
                  error={errors?.usagePerVoucher?.message}
                  disabled
                />
              )}
            />
          </FormGroup>

          {watchUsagePerVoucher === 'Limited' && (
            <FormGroup
              label="Enter limit amount (min 1)"
              error={errors?.limitAmount?.message}
            >
              <Input
                isInvalid={!!errors?.limitAmount?.message}
                {...register('limitAmount')}
                type="number"
                placeholder="1"
                disabled
              />
            </FormGroup>
          )}

          <FormGroup
            label="Nominated or Not?"
            error={errors?.nominated?.message}
          >
            <Controller
              name="nominated"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    { label: 'Yes', value: 'Yes' },
                    { label: 'No', value: 'No' },
                  ]}
                  placeholder="Select"
                  error={errors?.nominated?.message}
                  disabled
                />
              )}
            />
          </FormGroup>

          <Button
            variant="secondary"
            type="button"
            className="w-full"
            onClick={handleDownloadPdf}
          >
            Download PDF
            <FileDown className="w-4 h-4 ml-2" />
          </Button>

          <FormGroup
            label="Coins Expiry Date & Time"
            error={errors?.expiryDate?.message}
          >
            <DateTimePicker
              hasError={!!errors?.expiryDate?.message}
              value={watchExpiryDate ? new Date(watchExpiryDate) : undefined}
              onChange={(date) => {
                setValue(
                  'expiryDate',
                  date ? formatDateTime(date.valueOf()) : '',
                  { shouldValidate: true },
                )
              }}
              placeholder="2026-02-08 12:00"
            />
          </FormGroup>

          <FormGroup label="Active" direction="row">
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle="Voucher"
        deleteFunc={handleDeleteVoucher}
        deletedItem={voucher?.label}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
